// A company's screen figures (spec walkthrough 1, §5.2): the scanner's
// numbers, as the screen import stored them — keys are the datapoints' own
// names (the mono-mcp-workflows skill's investing.md maps them). Ratios are
// fractions there (0.68 is 68%), so the percentages are worked out here.
import { formatMarketCap } from "./format";

// The known figures, in the order a person reads them: price multiples,
// margins and returns, growth, size, ownership, then the code.
const FIGURES = [
  ["peRatio", "P/E", "multiple"],
  ["forwardPeRatio", "Forward P/E", "multiple"],
  ["pegRatio", "PEG", "multiple"],
  ["psRatio", "Price / sales", "multiple"],
  ["pfcfRatio", "Price / free cash flow", "multiple"],
  ["grossMargin", "Gross margin", "percent"],
  ["operatingMargin", "Operating margin", "percent"],
  ["netProfitMargin", "Net profit margin", "percent"],
  ["returnOnEquity", "Return on equity", "percent"],
  ["returnOnAssets", "Return on assets", "percent"],
  ["assetsTurnover", "Asset turnover", "multiple"],
  ["quarterlyRevenueGrowth", "Quarterly revenue growth", "percent"],
  ["annualEpsGrowth", "Annual EPS growth", "percent"],
  ["totalRevenue", "Revenue", "usd"],
  ["netIncome", "Net income", "usd"],
  ["insiderPercentOwned", "Owned by insiders", "percent"],
  ["institutionPercentOwned", "Owned by institutions", "percent"],
  ["morningstarIndustryCode", "Morningstar industry code", "text"],
];
const KNOWN = new Map(FIGURES.map(([key, label, kind], order) => [key, { label, kind, order }]));

// A real minus, as the scores use (formatScore).
const signed = (text) => text.replace(/^-/, "−");

/** Dollars as "$7.37B" / "−$988M" (formatMarketCap's units, with a sign). */
export function formatUsd(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  const text = formatMarketCap(Math.abs(amount));
  return amount < 0 ? `−${text}` : text;
}

function figureText(value, kind) {
  if (value === null || value === undefined || value === "") return "None";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value !== "number") return String(value);
  if (kind === "percent") {
    return signed(`${(value * 100).toLocaleString("en-US", { maximumFractionDigits: 1 })}%`);
  }
  if (kind === "usd") return formatUsd(value);
  return signed(value.toLocaleString("en-US", { maximumFractionDigits: 2 }));
}

/**
 * The figures to show, each `{key, label, text}`: the known ones first, in
 * FIGURES' order, then any other key under its own name (alphabetically).
 * A figure the scanner didn't have (null) says "None", never 0.
 */
export function screenFigures(screen) {
  const entries = Object.entries(screen && typeof screen === "object" ? screen : {});
  const rank = ([key]) => KNOWN.get(key)?.order ?? FIGURES.length;
  entries.sort((a, b) => rank(a) - rank(b) || a[0].localeCompare(b[0]));
  return entries.map(([key, value]) => {
    const known = KNOWN.get(key);
    return { key, label: known?.label ?? key, text: figureText(value, known?.kind) };
  });
}
